import { useEffect, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { DEFAULT_LOAN_TERM } from '../../../constants/loan'
import { formatCurrency } from '../../../helpers/formatters'
import { AffordabilityCalculatorDebtToIncomeLearnMoreModal } from './AffordabilityCalculatorDebtToIncomeLearnMoreModal'
import { Flex, Button, Heading, Text, Box, Progress, Slider, SliderLabel } from '@homebotapp/hb-react-component-catalog'

const DEBT_TO_INCOME_PROGRESS_BAR_VALUES = {
  affordable: 72,
  stretch: 14,
  difficult: 14
}

export const DEBT_TO_INCOME_DEFAULT = 36
const DEBT_TO_INCOME_HARD_THRESHOLD = 44
const DEBT_TO_INCOME_STRUGGLE_THRESHOLD = 37

export const MSG = defineMessages({
  homePrice: {
    id: 'AffordabilityCalculator.homePrice',
    defaultMessage: 'Home budget: {homePrice}'
  },
  payment: {
    id: 'AffordabilityCalculator.payment',
    defaultMessage: 'Monthly Payment: {monthlyPayment}'
  },
  paymentDescription: {
    id: 'AffordabilityCalculator.paymentDescription',
    defaultMessage: 'Assuming a {loanTerm} year loan at {interestRate}%, including estimated tax and insurance costs'
  },
  debtToIncome: {
    id: 'AffordabilityCalculator.debtToIncome',
    defaultMessage: 'Your debt-to-income ratio will determine how much you can afford.'
  },
  debtToIncomeComfortable: {
    id: 'AffordabilityCalculator.debtToIncomeComfortable',
    defaultMessage:
      'A debt-to-income (DTI) ratio of {dti} is a sign that you will be able to comfortably afford this loan.'
  },
  debtToIncomeStruggle: {
    id: 'AffordabilityCalculator.debtToIncomeStruggle',
    defaultMessage:
      'A debt-to-income (DTI) ratio of {dti} is a sign that you may struggle to comfortably make your payments.'
  },
  debtToIncomeHard: {
    id: 'AffordabilityCalculator.debtToIncomeStressful',
    defaultMessage:
      'A debt-to-income (DTI) ratio of {dti} is a sign that it may be hard to comfortably make your payments.'
  },
  learnMore: {
    id: 'AffordabilityCalculator.learnMore',
    defaultMessage: 'Learn more'
  },
  affordable: {
    id: 'AffordabilityCalculator.affordable',
    defaultMessage: 'Affordable'
  },
  stretch: {
    id: 'AffordabilityCalculator.stretch',
    defaultMessage: 'Stretch'
  },
  difficult: {
    id: 'AffordabilityCalculator.difficult',
    defaultMessage: 'Difficult'
  },
  progressBarAriaLabel: {
    id: 'AffordabilityCalculator.progressBarAriaLabel',
    defaultMessage: 'debt to income status bar'
  },
  increaseIncomeOrDecreaseExpenses: {
    id: 'AffordabilityCalculator.increaseIncomeOrDecreaseExpenses',
    defaultMessage: 'To comfortably afford a home, reduce your monthly debt obligations or increase your income.'
  }
})

export const calculateAffordableHome = ({
  yearlyIncome,
  monthlyExpenses,
  downPayment,
  interestRate, // e.g. 0.07 for 7%
  termYears = DEFAULT_LOAN_TERM,
  dtiLimit = DEBT_TO_INCOME_DEFAULT / 100
}) => {
  const monthlyIncome = yearlyIncome / 12
  const maxHousingBudget = monthlyIncome * dtiLimit - monthlyExpenses

  const r = interestRate / 12 // monthly rate
  const n = termYears * 12

  // Loan amount you can afford
  const loan = (maxHousingBudget * (1 - Math.pow(1 + r, -n))) / r

  // Add down payment to get max home price
  const maxHomePrice = loan + downPayment

  return {
    monthlyPayment: maxHousingBudget.toFixed(2),
    homePrice: maxHomePrice.toFixed(2)
  }
}

export interface AffordabilityCalculatorDebtToIncomeSectionProps {
  loanTerm: number
  downPayment: string
  interestRate: string
  yearlyIncome: string
  totalMonthlyExpenses: string
  setHomePrice: (value: number) => void
  setIsSliding: (value: boolean) => void
  setMonthlyPayment: (value: number) => void
  setDebtToIncomeRatio: (value: number) => void
  setHasUsedAffordabilityCalculator: (value: boolean) => void
}

export const AffordabilityCalculatorDebtToIncomeSection = ({
  loanTerm,
  downPayment,
  interestRate,
  yearlyIncome,
  setIsSliding,
  setHomePrice,
  setMonthlyPayment,
  totalMonthlyExpenses,
  setDebtToIncomeRatio,
  setHasUsedAffordabilityCalculator
}: AffordabilityCalculatorDebtToIncomeSectionProps) => {
  const intl = useIntl()
  const [showModal, setShowModal] = useState(false)
  const [calculatedHomePrice, setCalculatedHomePrice] = useState(0)
  const [calculatedMonthlyPayment, setCalculatedMonthlyPayment] = useState(0)
  const [debtToIncome, setDebtToIncome] = useState(
    localStorage?.getItem('affordabilityCalculatorDebtToIncome') || DEBT_TO_INCOME_DEFAULT.toString()
  )
  const debtToIncomeNumber = Number(debtToIncome)
  const calculationReturnsPositiveResult = calculatedHomePrice > 0 && calculatedMonthlyPayment > 0

  useEffect(() => {
    const { monthlyPayment: newMonthlyPayment, homePrice: newHomePrice } = calculateAffordableHome({
      yearlyIncome: Number(yearlyIncome),
      monthlyExpenses: Number(totalMonthlyExpenses),
      downPayment: Number(downPayment),
      interestRate: Number(interestRate) / 100,
      termYears: loanTerm,
      dtiLimit: Number(debtToIncome) / 100
    })

    setHomePrice(Number(newHomePrice))
    setMonthlyPayment(Number(newMonthlyPayment))
    setCalculatedHomePrice(Number(newHomePrice))
    setCalculatedMonthlyPayment(Number(newMonthlyPayment))
  }, [loanTerm, downPayment, interestRate, yearlyIncome, totalMonthlyExpenses])

  return (
    <>
      <Box
        opacity={calculationReturnsPositiveResult ? 1 : 0.125}
        pointerEvents={calculationReturnsPositiveResult ? 'auto' : 'none'}
      >
        <Heading as='h2' size={['sm', null, null, 'md']} textAlign='left'>
          {intl.formatMessage(MSG.homePrice, {
            homePrice: (
              <Text
                as='span'
                fontWeight='bold'
                color={
                  calculatedHomePrice < 0 || calculatedMonthlyPayment < 0
                    ? 'neutral.700'
                    : debtToIncomeNumber < DEBT_TO_INCOME_STRUGGLE_THRESHOLD
                    ? 'success.600'
                    : debtToIncomeNumber < DEBT_TO_INCOME_HARD_THRESHOLD
                    ? 'warning.600'
                    : 'danger.600'
                }
              >
                {calculationReturnsPositiveResult ? formatCurrency(calculatedHomePrice) : '—'}
              </Text>
            )
          })}
        </Heading>
        <Box>
          <Heading mb={0} as='h3' size={['xs', null, null, 'sm']} textAlign='left'>
            {intl.formatMessage(MSG.payment, {
              monthlyPayment: (
                <Text
                  as='span'
                  fontWeight='bold'
                  color={
                    calculatedHomePrice < 0 || calculatedMonthlyPayment < 0
                      ? 'neutral.700'
                      : debtToIncomeNumber < DEBT_TO_INCOME_STRUGGLE_THRESHOLD
                      ? 'success.600'
                      : debtToIncomeNumber < DEBT_TO_INCOME_HARD_THRESHOLD
                      ? 'warning.600'
                      : 'danger.600'
                  }
                >
                  {calculatedMonthlyPayment > 0 && calculatedHomePrice > 0
                    ? formatCurrency(calculatedMonthlyPayment)
                    : '—'}
                </Text>
              )
            })}
          </Heading>
          {Number(calculatedMonthlyPayment) !== 0 && (
            <Text size='2xs' textAlign='left'>
              {intl.formatMessage(MSG.paymentDescription, {
                loanTerm: loanTerm,
                interestRate: interestRate
              })}
            </Text>
          )}
        </Box>
        {!!debtToIncome && calculationReturnsPositiveResult ? (
          <Text size='sm' textAlign='left'>
            {intl.formatMessage(
              debtToIncomeNumber < DEBT_TO_INCOME_STRUGGLE_THRESHOLD
                ? MSG.debtToIncomeComfortable
                : debtToIncomeNumber < DEBT_TO_INCOME_HARD_THRESHOLD
                ? MSG.debtToIncomeStruggle
                : MSG.debtToIncomeHard,
              {
                dti: (
                  <Text
                    as='span'
                    fontWeight='bold'
                    color={
                      debtToIncomeNumber < DEBT_TO_INCOME_STRUGGLE_THRESHOLD
                        ? 'success.600'
                        : debtToIncomeNumber < DEBT_TO_INCOME_HARD_THRESHOLD
                        ? 'warning.600'
                        : 'danger.600'
                    }
                  >
                    {debtToIncome}%
                  </Text>
                )
              }
            )}
          </Text>
        ) : calculatedHomePrice === 0 && calculatedMonthlyPayment === 0 ? (
          <Text size='sm' textAlign='left'>
            {intl.formatMessage(MSG.debtToIncome)}
          </Text>
        ) : (
          <Text size='sm' color='neutral.900' fontWeight='semibold' textAlign='left'>
            {intl.formatMessage(MSG.increaseIncomeOrDecreaseExpenses)}
          </Text>
        )}
        <Button
          mb={8}
          size='sm'
          variant='link'
          colorScheme='primary'
          justifyContent='flex-start'
          onClick={() => setShowModal(true)}
          tracking={{
            descriptor: 'Learn more',
            guid: 'pgihiFTnRijMetgb39wdKR',
            ui_context: 'Affordability Calculator Page'
          }}
        >
          {intl.formatMessage(MSG.learnMore)}
        </Button>
        <Box>
          <Progress
            min={0}
            max={100}
            variant='multiSegment'
            aria-label={intl.formatMessage(MSG.progressBarAriaLabel)}
            values={{
              'success.600': DEBT_TO_INCOME_PROGRESS_BAR_VALUES.affordable,
              'warning.500': DEBT_TO_INCOME_PROGRESS_BAR_VALUES.stretch,
              'danger.500': DEBT_TO_INCOME_PROGRESS_BAR_VALUES.difficult
            }}
          />
          <Slider
            min={0}
            max={50}
            size='lg'
            value={debtToIncomeNumber}
            onChangeEnd={() => setIsSliding(false)}
            onChangeStart={() => setIsSliding(true)}
            onFocus={() => setHasUsedAffordabilityCalculator(true)}
            onChange={value => {
              const { monthlyPayment: newMonthlyPayment, homePrice: newHomePrice } = calculateAffordableHome({
                yearlyIncome: Number(yearlyIncome),
                monthlyExpenses: Number(totalMonthlyExpenses),
                downPayment: Number(downPayment),
                interestRate: Number(interestRate) / 100,
                termYears: loanTerm,
                dtiLimit: value / 100
              })

              if (
                (Number(newHomePrice) > 0 && Number(newMonthlyPayment) > 0) ||
                Number(localStorage?.getItem('affordabilityCalculatorDebtToIncome')) < value
              ) {
                setDebtToIncome(value.toString())
                setDebtToIncomeRatio(value)
                setHomePrice(Number(newHomePrice))
                setMonthlyPayment(Number(newMonthlyPayment))
                setCalculatedHomePrice(Number(newHomePrice))
                setCalculatedMonthlyPayment(Number(newMonthlyPayment))
                localStorage?.setItem('affordabilityCalculatorDebtToIncome', value.toString())
              }
            }}
            name='debtToIncome'
            transform='translateY(-16px)'
            colorScheme={
              debtToIncomeNumber < DEBT_TO_INCOME_STRUGGLE_THRESHOLD
                ? 'success'
                : debtToIncomeNumber < DEBT_TO_INCOME_HARD_THRESHOLD
                ? 'warning'
                : 'danger'
            }
            css={{
              '& .chakra-slider__track': {
                height: '0px'
              }
            }}
            tracking={{
              descriptor: 'Debt to income slider',
              guid: '1z3uFHf55b8Wz9UzHpf7x7',
              ui_context: 'Affordability Calculator Page'
            }}
          >
            <SliderLabel value={debtToIncomeNumber}>
              <Text
                as='span'
                size='xs'
                whiteSpace='nowrap'
                fontWeight='semibold'
                color={
                  debtToIncomeNumber < DEBT_TO_INCOME_STRUGGLE_THRESHOLD
                    ? 'success.600'
                    : debtToIncomeNumber < DEBT_TO_INCOME_HARD_THRESHOLD
                    ? 'warning.600'
                    : 'danger.600'
                }
              >
                {debtToIncome}%{' '}
                <Text as='span' size='2xs' color='neutral.900'>
                  DTI
                </Text>
              </Text>
            </SliderLabel>
          </Slider>
          <Flex mt={-4} direction='row'>
            <Text size='2xs' fontWeight='semibold' flex='1 1 72%' color='success.700'>
              {intl.formatMessage(MSG.affordable)}
            </Text>
            <Text size='2xs' fontWeight='semibold' flex='1 1 14%' color='warning.700'>
              {intl.formatMessage(MSG.stretch)}
            </Text>
            <Text size='2xs' fontWeight='semibold' flex='1 1 14%' color='danger.700'>
              {intl.formatMessage(MSG.difficult)}
            </Text>
          </Flex>
        </Box>
      </Box>
      {calculatedHomePrice <= 0 && calculatedMonthlyPayment <= 0 && (
        <Box mt='-180px' width='80%' mb={16} mx='auto'>
          <Text color='neutral.900' fontWeight='semibold' textAlign='center'>
            {intl.formatMessage(MSG.increaseIncomeOrDecreaseExpenses)}
          </Text>
        </Box>
      )}
      {showModal && <AffordabilityCalculatorDebtToIncomeLearnMoreModal onClose={() => setShowModal(false)} />}
    </>
  )
}
